import React, { useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  InputAdornment,
  Button,
  Autocomplete,
  TextField,
  type SelectChangeEvent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import type { ValuationAsset, Entity } from '../types/valuation';
import { generateAssetTypes } from '../data/assetTypes';
import { generateAssetSectors } from '../data/assetSubSectors';
import { generateClassificationTrees, getTreeLevels, getTreeItemsByLevel } from '../data/classificationTrees';

export interface FilterConfig {
  entities: string[];
  assetClasses: string[];
  sectors: string[];
  portfolios: string[];
  classificationTree: {
    treeId: number | null;
    level: number | null;
    itemIds: number[];
  } | null;
  asAtDate: Date;
  reportable: 'all' | 'reportable' | 'non-reportable';
}

interface FilterSelectorProps {
  assets: ValuationAsset[];
  entities: Entity[];
  filters: FilterConfig;
  onFiltersChange: (filters: FilterConfig) => void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const FilterSelector: React.FC<FilterSelectorProps> = ({
  assets,
  entities,
  filters,
  onFiltersChange
}) => {
  // Extract unique asset types and sectors from assets
  const { availableAssetClasses, availablePortfolios, availableSectorsAndSubSectors, availableClassificationTrees } = useMemo(() => {
    const assetTypes = generateAssetTypes();
    const assetSubSectors = generateAssetSectors();
    const classificationTrees = generateClassificationTrees();
    
    const assetTypeMap = new Map<number, string>();
    assetTypes.forEach(type => {
      assetTypeMap.set(type.id, type.name);
    });
    
    const assetSubSectorMap = new Map<number, string>();
    assetSubSectors.forEach(subSector => {
      assetSubSectorMap.set(subSector.id, subSector.name);
    });

    // Create sector mapping based on sectorId
    const sectorMap = new Map<number, string>([
      [1, 'Alternative Investments'],
      [2, 'Equities'],
      [3, 'Fixed Income'],
      [4, 'Cash & Cash Equivalents'],
      [5, 'Real Estate'],
      [6, 'Diversified Funds'],
      [7, 'Digital Assets'],
      [8, 'Commodities'],
      [9, 'Other'],
      [10, 'Tangible Assets'],
      [11, 'Private Markets']
    ]);

    const assetClasses = new Set<string>();
    const usedSubSectors = new Set<number>();
    const portfolios = new Set<string>();

    assets.forEach(asset => {
      if (asset.assetTypeId) {
        const assetTypeName = assetTypeMap.get(asset.assetTypeId);
        if (assetTypeName) {
          assetClasses.add(assetTypeName);
        }
      }
      if (asset.assetSubSectorId) {
        usedSubSectors.add(asset.assetSubSectorId);
      }
      if (asset.portfolioName) {
        portfolios.add(asset.portfolioName);
      }
    });

    // Group subsectors by sector
    const sectorGroups = new Map<string, string[]>();
    
    assetSubSectors.forEach(subSector => {
      if (usedSubSectors.has(subSector.id)) {
        const sectorName = sectorMap.get(subSector.sectorId) || 'Unknown Sector';
        if (!sectorGroups.has(sectorName)) {
          sectorGroups.set(sectorName, []);
        }
        sectorGroups.get(sectorName)!.push(subSector.name);
      }
    });

    // Create flat list with sectors and indented subsectors
    const sectorsAndSubSectors: { value: string; label: string; isSubSector: boolean }[] = [];
    
    Array.from(sectorGroups.keys()).sort().forEach(sectorName => {
      // Add sector
      sectorsAndSubSectors.push({
        value: sectorName,
        label: sectorName,
        isSubSector: false
      });
      
      // Add subsectors (sorted)
      const subSectors = sectorGroups.get(sectorName)!.sort();
      subSectors.forEach(subSectorName => {
        sectorsAndSubSectors.push({
          value: subSectorName,
          label: subSectorName,
          isSubSector: true
        });
      });
    });

    // Generate sector tree items
    // (Removed - sector tree filtering has been removed)

    return {
      availableAssetClasses: Array.from(assetClasses).sort(),
      availablePortfolios: Array.from(portfolios).sort(),
      availableSectorsAndSubSectors: sectorsAndSubSectors,
      availableClassificationTrees: classificationTrees
    };
  }, [assets]);

  const handleEntityChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      entities: typeof value === 'string' ? value.split(',') : value
    });
  };

  const handleSectorChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      sectors: typeof value === 'string' ? value.split(',') : value
    });
  };

  const handleDateChange = (newDate: Date | null) => {
    if (newDate) {
      onFiltersChange({
        ...filters,
        asAtDate: newDate
      });
    }
  };

  const handleClassificationTreeChange = (treeId: number | null) => {
    onFiltersChange({
      ...filters,
      classificationTree: treeId ? { treeId, level: null, itemIds: [] } : null
    });
  };

  const handleClassificationLevelChange = (level: number | null) => {
    onFiltersChange({
      ...filters,
      classificationTree: filters.classificationTree ? {
        ...filters.classificationTree,
        level,
        itemIds: [] // Reset items when level changes
      } : null
    });
  };

  const handleClassificationItemsChange = (itemIds: number[]) => {
    onFiltersChange({
      ...filters,
      classificationTree: filters.classificationTree ? {
        ...filters.classificationTree,
        itemIds
      } : null
    });
  };

  const getFilterSummary = () => {
    const activeFilters = [];
    const today = new Date();
    const isToday = filters.asAtDate.toDateString() === today.toDateString();
    
    if (!isToday) {
      activeFilters.push(`As at ${filters.asAtDate.toLocaleDateString()}`);
    }
    
    // Always show the reportable filter status
    if (filters.reportable === 'reportable') {
      activeFilters.push('reportable assets only');
    } else if (filters.reportable === 'non-reportable') {
      activeFilters.push('non-reportable assets only');
    } else {
      activeFilters.push('both reportable and non-reportable');
    }
    
    if (filters.entities.length > 0) {
      activeFilters.push(`${filters.entities.length} ${filters.entities.length === 1 ? 'entity' : 'entities'}`);
    }
    if (filters.assetClasses.length > 0) {
      activeFilters.push(`${filters.assetClasses.length} asset ${filters.assetClasses.length === 1 ? 'type' : 'types'}`);
    }
    if (filters.sectors.length > 0) {
      activeFilters.push(`${filters.sectors.length} ${filters.sectors.length === 1 ? 'sector' : 'sectors'}`);
    }
    if (filters.portfolios.length > 0) {
      activeFilters.push(`${filters.portfolios.length} ${filters.portfolios.length === 1 ? 'portfolio' : 'portfolios'}`);
    }
    if (filters.classificationTree && filters.classificationTree.itemIds.length > 0) {
      activeFilters.push(`${filters.classificationTree.itemIds.length} classification ${filters.classificationTree.itemIds.length === 1 ? 'item' : 'items'}`);
    }

    return `Filtered by: ${activeFilters.join(', ')}`;
  };

  const getTotalActiveFilters = () => {
    const classificationCount = filters.classificationTree?.itemIds.length || 0;
    return filters.entities.length + filters.assetClasses.length + filters.sectors.length + filters.portfolios.length + classificationCount;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Accordion defaultExpanded={false} sx={{ boxShadow: 0, border: '1px solid', borderColor: 'grey.300', bgcolor: 'white' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: 'white',
            '&:hover': { bgcolor: 'grey.50' },
            minHeight: 48,
            '& .MuiAccordionSummary-content': {
              alignItems: 'center'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'medium', minWidth: 'fit-content' }}>
              Filters:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getFilterSummary()}
            </Typography>
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ pt: 2 }}>
          {/* Date and Reportable Filters Row */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
            gap: 3,
            mb: 3 
          }}>
            {/* Date Filter */}
            <Box sx={{ position: 'relative' }}>
              <DatePicker
                label="As at Date"
                value={filters.asAtDate}
                onChange={handleDateChange}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'medium',
                    fullWidth: true
                  }
                }}
              />
            </Box>
            
            {/* Reportable Filter */}
            <FormControl fullWidth size="medium">
              <InputLabel>Reportable Status</InputLabel>
              <Select
                value={filters.reportable}
                onChange={(event) => onFiltersChange({ 
                  ...filters, 
                  reportable: event.target.value as 'all' | 'reportable' | 'non-reportable' 
                })}
                input={<OutlinedInput label="Reportable Status" />}
              >
                <MenuItem value="reportable">Reportable assets only</MenuItem>
                <MenuItem value="non-reportable">Non-reportable assets only</MenuItem>
                <MenuItem value="all">Reportable and non-reportable assets</MenuItem>
              </Select>
            </FormControl>

            {/* Empty third column */}
            <Box />
          </Box>

          {/* Entity, Asset Types, and Sectors Row */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
            gap: 3 
          }}>
            {/* Entity Filter */}
            <FormControl fullWidth sx={{ position: 'relative' }}>
                <InputLabel>Entities</InputLabel>
                <Select
                  multiple
                  value={filters.entities}
                  onChange={handleEntityChange}
                  input={<OutlinedInput label="Entities" />}
                  endAdornment={
                    filters.entities.length > 0 && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFiltersChange({ ...filters, entities: [] });
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{ mr: 1 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => {
                        const entity = entities.find(e => e.id === value);
                        return (
                          <Chip 
                            key={value} 
                            label={entity?.name || value} 
                            size="small" 
                            variant="outlined"
                            onDelete={(event) => {
                              event.stopPropagation();
                              const newEntities = filters.entities.filter(id => id !== value);
                              onFiltersChange({ ...filters, entities: newEntities });
                            }}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {entities.map((entity) => (
                    <MenuItem key={entity.id} value={entity.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {entity.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entity.type}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

            {/* Portfolio Filter */}
            <Box sx={{ position: 'relative' }}>
                <Autocomplete
                  multiple
                  options={availablePortfolios}
                  value={filters.portfolios}
                  onChange={(_event, newValue) => {
                    onFiltersChange({ ...filters, portfolios: newValue });
                  }}
                  filterSelectedOptions
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Portfolios"
                      placeholder="Search portfolios..."
                    />
                  )}
                  sx={{ 
                    '& .MuiAutocomplete-tag': {
                      maxWidth: '200px'
                    }
                  }}
                />
              </Box>

            {/* Asset Type Filter */}
            <Box sx={{ position: 'relative' }}>
                <Autocomplete
                  multiple
                  options={availableAssetClasses}
                  value={filters.assetClasses}
                  onChange={(_event, newValue) => {
                    onFiltersChange({ ...filters, assetClasses: newValue });
                  }}
                  filterSelectedOptions
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Asset Types"
                      placeholder="Search asset types..."
                    />
                  )}
                  sx={{ 
                    '& .MuiAutocomplete-tag': {
                      maxWidth: '200px'
                    }
                  }}
                />
              </Box>

            {/* Sector Filter */}
            <FormControl fullWidth sx={{ position: 'relative' }}>
                <InputLabel>Sectors</InputLabel>
                <Select
                  multiple
                  value={filters.sectors}
                  onChange={handleSectorChange}
                  input={<OutlinedInput label="Sectors" />}
                  endAdornment={
                    filters.sectors.length > 0 && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFiltersChange({ ...filters, sectors: [] });
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{ mr: 1 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip 
                          key={value} 
                          label={value} 
                          size="small" 
                          variant="outlined"
                          onDelete={(event) => {
                            event.stopPropagation();
                            const newSectors = filters.sectors.filter(s => s !== value);
                            onFiltersChange({ ...filters, sectors: newSectors });
                          }}
                          onMouseDown={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {availableSectorsAndSubSectors.map((item, index) => (
                    <MenuItem 
                      key={`${item.value}-${index}`} 
                      value={item.value}
                      sx={{ 
                        pl: item.isSubSector ? 4 : 2,
                        fontWeight: item.isSubSector ? 'normal' : 'medium',
                        fontSize: item.isSubSector ? '0.875rem' : '1rem',
                        color: item.isSubSector ? 'text.secondary' : 'text.primary'
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            
            {/* Empty third column */}
            <Box />
          </Box>

          {/* Classification Tree Filters Row */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
            gap: 3,
            mt: 3 
          }}>
            {/* Step 1: Select Tree */}
            <FormControl fullWidth sx={{ position: 'relative' }}>
              <InputLabel>Classification tree</InputLabel>
              <Select
                value={filters.classificationTree?.treeId || ''}
                onChange={(e) => handleClassificationTreeChange(e.target.value ? Number(e.target.value) : null)}
                input={<OutlinedInput label="Classification tree" />}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {availableClassificationTrees.map((tree) => (
                  <MenuItem key={tree.id} value={tree.id}>
                    {tree.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Step 2: Select Level (only if tree is selected) */}
            {filters.classificationTree?.treeId && (
              <FormControl fullWidth sx={{ position: 'relative' }}>
                <InputLabel>Classification tree level</InputLabel>
                <Select
                  value={filters.classificationTree?.level || ''}
                  onChange={(e) => handleClassificationLevelChange(e.target.value ? Number(e.target.value) : null)}
                  input={<OutlinedInput label="Classification tree level" />}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {getTreeLevels(filters.classificationTree.treeId).map((level) => (
                    <MenuItem key={level} value={level}>
                      Level {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Step 3: Select Items (only if tree and level are selected) */}
            {filters.classificationTree?.treeId && filters.classificationTree?.level && (
              <FormControl fullWidth sx={{ position: 'relative' }}>
                <InputLabel>Classification tree item</InputLabel>
                <Select
                  multiple
                  value={filters.classificationTree?.itemIds || []}
                  onChange={(e) => handleClassificationItemsChange(e.target.value as number[])}
                  input={<OutlinedInput label="Classification tree item" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((itemId) => {
                        const item = getTreeItemsByLevel(filters.classificationTree!.treeId!, filters.classificationTree!.level!)
                          .find(i => i.id === itemId);
                        return (
                          <Chip 
                            key={itemId} 
                            label={item?.name || `Item ${itemId}`} 
                            size="small" 
                            variant="outlined"
                            onDelete={(event) => {
                              event.stopPropagation();
                              const newItemIds = filters.classificationTree!.itemIds.filter(id => id !== itemId);
                              handleClassificationItemsChange(newItemIds);
                            }}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {getTreeItemsByLevel(filters.classificationTree.treeId, filters.classificationTree.level).map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {/* Clear All Filters Option */}
          {getTotalActiveFilters() > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => onFiltersChange({ entities: [], assetClasses: [], sectors: [], portfolios: [], classificationTree: null, asAtDate: new Date(), reportable: 'reportable' })}
              >
                Clear All Filters ({getTotalActiveFilters()})
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default FilterSelector;
